import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Check, Circle } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/date";
import { ROLE_LABEL } from "@/components/TeamUsersCard";
import BusinessMilestones from "@/components/milestones/BusinessMilestones";

const TAB = "rounded-full data-[state=active]:bg-[#0A1F44] data-[state=active]:text-white text-xs sm:text-sm";

function BootcampSummary({ bp }) {
  const done = Object.entries(bp.days).sort((a, b) => Number(a[0]) - Number(b[0]));
  return (
    <Card className="p-5 space-y-3" data-testid="profile-bootcamp">
      <div className="flex items-baseline justify-between">
        <span className="text-sm"><span className="font-serif text-2xl text-[#0A1F44]">{bp.completed}</span> / {bp.total} days</span>
        <span className="text-sm text-slate-600" data-testid="profile-bootcamp-pct">{bp.pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#C9A961]" style={{ width: `${bp.pct}%` }} /></div>
      {done.length ? (
        <div className="flex flex-wrap gap-1.5">
          {done.map(([d, at]) => (
            <span key={d} title={fmtDateTime(at)} className="text-[11px] px-2 py-0.5 rounded-full bg-[#0A1F44]/5 text-[#0A1F44]">Day {d} · {fmtDate(at)}</span>
          ))}
        </div>
      ) : <p className="text-xs text-slate-500">No days completed yet.</p>}
    </Card>
  );
}

function Onboarding({ pbo }) {
  if (!pbo) {
    return <Card className="p-5 text-sm text-slate-500" data-testid="profile-no-pbo">No PBO record linked yet. An Admin can link it from Admin → Link PBO to partner.</Card>;
  }
  return (
    <Card className="p-5 space-y-4" data-testid="profile-onboarding">
      <div className="text-sm">
        <div className="font-medium text-slate-900">PBO record: {pbo.name}</div>
        <div className="text-xs text-slate-500">Launched {fmtDate(pbo.launch_date)} · sponsored by {pbo.ibo_partner_name || "—"}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Post-launch checklist</div>
        <ul className="grid sm:grid-cols-2 gap-1.5">
          {(pbo.checklist || []).map((c) => (
            <li key={c.name} className="flex items-center gap-2 text-sm" data-testid={`profile-checklist-${c.name}`}>
              {c.done ? <Check className="w-4 h-4 text-[#C9A961]" /> : <Circle className="w-4 h-4 text-slate-300" />}
              <span>{c.name}</span>
              {c.done && <span className="text-[11px] text-slate-500">{fmtDate(c.completed_at)}</span>}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Stage history</div>
        <ul className="space-y-1">
          {(pbo.stage_history || []).map((h, i) => (
            <li key={i} className="text-xs text-slate-700">{h.stage} · {fmtDate(h.entered_at)}{h.coaches?.length ? ` · ${h.coaches.join(", ")}` : ""}</li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

export default function PartnerProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/people/${id}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load profile"));
  }, [id]);
  if (err) return <div className="text-sm text-[#8A1F1F] py-10 text-center" data-testid="profile-error">{err}</div>;
  if (!data) return <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>;
  const u = data.user;
  return (
    <div className="space-y-6" data-testid="partner-profile-page">
      <section className="rounded-2xl bg-[#0A1F44] text-white p-5 sm:p-7 ring-1 ring-[#C9A961]/30">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Partner profile</div>
        <h1 className="font-serif text-3xl sm:text-4xl mt-1" data-testid="profile-name">{u.name}</h1>
        <p className="text-white/70 text-sm mt-1">{ROLE_LABEL[u.role] || u.role} · {u.upline_team || "No team"}{u.nearest_upline_leader_name ? ` · Upline: ${u.nearest_upline_leader_name}` : ""}</p>
      </section>
      <Tabs defaultValue="milestones">
        <TabsList className="bg-white border border-slate-200 rounded-full p-1 h-auto">
          <TabsTrigger value="milestones" className={TAB} data-testid="profile-tab-milestones">Milestones</TabsTrigger>
          <TabsTrigger value="bootcamp" className={TAB} data-testid="profile-tab-bootcamp">Bootcamp</TabsTrigger>
          <TabsTrigger value="onboarding" className={TAB} data-testid="profile-tab-onboarding">Onboarding</TabsTrigger>
        </TabsList>
        <TabsContent value="milestones" className="mt-5"><BusinessMilestones userId={u.id} /></TabsContent>
        <TabsContent value="bootcamp" className="mt-5"><BootcampSummary bp={data.bootcamp} /></TabsContent>
        <TabsContent value="onboarding" className="mt-5"><Onboarding pbo={data.linked_pbo} /></TabsContent>
      </Tabs>
    </div>
  );
}
